// foregroundNotify — local notifications fired from realtime subscriptions
// while the app tab is open and focused. Web Push only fires when the
// service worker can wake the page (typically backgrounded or closed); for
// the foreground case we drive ServiceWorkerRegistration.showNotification
// directly so the user still gets a system notification toast.
//
// Each subscription pulls the user's notification_preferences row first
// (cached after first read) so the same toggles that gate Web Push also
// gate this path.
//
// Usage:
//   const stop = subscribeForegroundNotifications({ user, activeEventId, profile })
//   // ...later
//   stop()

import { supabase } from './supabaseClient'

let cachedPrefs = null
let cachedPrefsUserId = null

const DEFAULTS = {
  kill_pending:    true,
  bounty_on_you:   true,
  frago_assigned:  true,
  frago_broadcast: true,
  intel_found:     true,
  game_status:     true,
  teammate_killed: false,
  bounty_claimed:  false,
}

async function getPrefs(userId) {
  if (cachedPrefsUserId === userId && cachedPrefs) return cachedPrefs
  const { data } = await supabase
    .from('notification_preferences').select('*').eq('user_id', userId).limit(1)
  cachedPrefsUserId = userId
  cachedPrefs = data?.[0] || null
  return cachedPrefs
}

function prefAllows(pref, eventType) {
  if (pref && eventType in pref) return !!pref[eventType]
  return DEFAULTS[eventType] ?? true
}

// Show a local notification — falls back to Notification ctor when the SW
// isn't ready (avoids the "no service worker" error on first session).
async function show(title, body, url) {
  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
    const reg = await navigator.serviceWorker.getRegistration('/sw.js')
    if (reg && reg.active) {
      await reg.showNotification(title, {
        body,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        vibrate: [200, 100, 200],
        data: { url: url || '/play' },
      })
    } else {
      new Notification(title, { body, icon: '/icon-192.png' })
    }
  } catch (err) {
    console.warn('[foregroundNotify] show failed', err)
  }
}

// Subscribe — returns an unsubscribe function. Safe to call repeatedly:
// re-subscribing replaces the channel so we don't double-fire.
export function subscribeForegroundNotifications({ user, activeEventId }) {
  if (!user?.id || !activeEventId) return () => {}

  const channel = supabase.channel(`foreground-notify-${user.id}-${activeEventId}`)
    // kill_log INSERT — notify when this user is the target of a fresh kill scan
    .on('postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'kill_log', filter: `target_id=eq.${user.id}` },
      async (payload) => {
        if (payload.new?.event_id !== activeEventId) return
        if (payload.new?.status !== 'pending') return
        const pref = await getPrefs(user.id)
        if (!prefAllows(pref, 'kill_pending')) return
        const { data } = await supabase
          .from('users').select('callsign, display_name').eq('id', payload.new.killer_id).limit(1)
        const name = data?.[0]?.callsign || data?.[0]?.display_name || 'An operator'
        show('Kill awaiting honor', `${name} scanned you. Confirm or dishonor.`, '/play')
      })
    // bounties INSERT (target = this user, non-FRAGO)
    .on('postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'bounties', filter: `target_id=eq.${user.id}` },
      async (payload) => {
        if (payload.new?.event_id !== activeEventId) return
        if (payload.new?.category === 'frago') return
        const pref = await getPrefs(user.id)
        if (!prefAllows(pref, 'bounty_on_you')) return
        show('Bounty placed on you',
          `A ${payload.new.bounty_type || 'kill'} bounty went up — ${payload.new.points || 0} pts`,
          `/bounty/${payload.new.id}`)
      })
    // intel_trigger_scans INSERT — notify the SCANNER'S faction commander
    // (Bug 27 third report). Matches the server-side notify_intel_found
    // recipient list: viewer qualifies if they are either
    //   (a) event_role='co_gm' AND co_gm_faction_id = scan.faction_id, OR
    //   (b) the MCS top-of-tree (parent_id IS NULL) assigned_player_id for
    //       the scanner's faction in this event.
    // Full event_role='gm' viewers intentionally skipped — they get the
    // SITREP toast surface separately and don't need a per-scan push.
    .on('postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'intel_trigger_scans' },
      async (payload) => {
        const scan = payload.new
        if (!scan) return
        const { data: trgRows } = await supabase
          .from('intel_triggers')
          .select('event_id, message').eq('id', scan.trigger_id).limit(1)
        const trg = trgRows?.[0]
        if (!trg || trg.event_id !== activeEventId) return

        // Path (a): matching-faction co-GM in this event
        const { data: epRows } = await supabase
          .from('event_players')
          .select('event_role, co_gm_faction_id')
          .eq('event_id', activeEventId).eq('user_id', user.id).limit(1)
        const ep = epRows?.[0]
        const isMatchingCoGm =
          ep?.event_role === 'co_gm' && ep?.co_gm_faction_id === scan.faction_id

        // Path (b): MCS faction-CO (top-of-tree node assigned to viewer)
        let isFactionCo = false
        if (!isMatchingCoGm) {
          const { data: coRows } = await supabase
            .from('mcs_nodes')
            .select('id')
            .eq('event_id', activeEventId)
            .eq('faction_id', scan.faction_id)
            .is('parent_id', null)
            .eq('assigned_player_id', user.id)
            .limit(1)
          isFactionCo = !!coRows?.[0]
        }

        if (!isMatchingCoGm && !isFactionCo) return
        const pref = await getPrefs(user.id)
        if (!prefAllows(pref, 'intel_found')) return
        const body = trg.message
          ? (trg.message.length > 80 ? `${trg.message.slice(0, 77)}…` : trg.message)
          : 'A field-intel QR was scanned by your faction.'
        show('Intel acquired', body, '/dashboard')
      })
    // FRAGO Fix 6 — bounties UPDATE for FRAGO activations. Web Push
    // already handles backgrounded tabs via dispatch_push_many in the
    // cascade trigger; this path covers the foreground (tab open)
    // case. Viewer qualifies as commander via the same UNION the
    // server-side push uses: co_gm scoped to target_faction OR root
    // MCS node holder for that faction OR full GM scoped to it.
    // commander_message wins for the body, falls back to
    // activation_message.
    .on('postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'bounties', filter: `event_id=eq.${activeEventId}` },
      async (payload) => {
        const oldRow = payload.old
        const newRow = payload.new
        if (!newRow || newRow.category !== 'frago') return
        if (oldRow?.status === newRow.status) return
        if (newRow.status !== 'active') return
        if (!newRow.commander_notify) return

        // Faction-scope check — is the viewer a commander for this FRAGO?
        const factionId = newRow.target_faction_id
        // co_gm scoped to target_faction (event-wide broadcast → any co_gm)
        const { data: epRows } = await supabase
          .from('event_players')
          .select('event_role, co_gm_faction_id, faction_id')
          .eq('event_id', activeEventId).eq('user_id', user.id).limit(1)
        const ep = epRows?.[0]
        const isCoGmMatch = ep?.event_role === 'co_gm' &&
          (!factionId || ep?.co_gm_faction_id === factionId || ep?.faction_id === factionId)
        const isGmMatch = ep?.event_role === 'gm' &&
          (!factionId || ep?.faction_id === factionId)
        // Root MCS node holder for the target faction.
        let isFactionCo = false
        if (!isCoGmMatch && !isGmMatch) {
          const { data: coRows } = await supabase
            .from('mcs_nodes')
            .select('id')
            .eq('event_id', activeEventId)
            .is('parent_id', null)
            .eq('assigned_player_id', user.id)
            .limit(factionId ? 1 : 1)
          // If a target faction is set, require the node faction to match.
          if (coRows?.[0]) {
            if (!factionId) isFactionCo = true
            else {
              const { data: nodeRows } = await supabase
                .from('mcs_nodes').select('faction_id').eq('id', coRows[0].id).limit(1)
              isFactionCo = nodeRows?.[0]?.faction_id === factionId
            }
          }
        }
        if (!isCoGmMatch && !isGmMatch && !isFactionCo) return

        const pref = await getPrefs(user.id)
        if (!prefAllows(pref, 'frago_broadcast')) return
        const body = newRow.commander_message || newRow.activation_message ||
          `${newRow.mission_name || 'A FRAGO'} just activated.`
        show(newRow.mission_name || 'FRAGO activated', body, `/frago/${newRow.id}`)
      })
    // FRAGO Fix 6 — frago_objectives UPDATE for per-objective
    // commander notify. Server-side fire_frago_objective_completed
    // already dispatches Web Push; this is the foreground twin so the
    // commander sees a local toast when their tab is open. Only fires
    // for who='commander' notify_config rows — 'faction' and 'all'
    // routes still rely on the Web Push path (every player in scope
    // would otherwise get a redundant local toast).
    .on('postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'frago_objectives' },
      async (payload) => {
        const oldRow = payload.old
        const newRow = payload.new
        if (!newRow) return
        if (oldRow?.status === newRow.status) return
        if (newRow.status !== 'completed') return
        if (!newRow.notify_config) return
        if (newRow.notify_config.who !== 'commander') return
        if (!newRow.notify_config.message) return

        // Resolve the parent FRAGO to scope-check the viewer.
        const { data: phaseRows } = await supabase
          .from('frago_phases').select('frago_id').eq('id', newRow.phase_id).limit(1)
        const fragoId = phaseRows?.[0]?.frago_id
        if (!fragoId) return
        const { data: fragoRows } = await supabase
          .from('bounties').select('event_id, target_faction_id, mission_name')
          .eq('id', fragoId).limit(1)
        const frago = fragoRows?.[0]
        if (!frago || frago.event_id !== activeEventId) return
        const factionId = frago.target_faction_id

        // Commander scope check — same UNION as the activation path.
        const { data: epRows } = await supabase
          .from('event_players')
          .select('event_role, co_gm_faction_id, faction_id')
          .eq('event_id', activeEventId).eq('user_id', user.id).limit(1)
        const ep = epRows?.[0]
        const isCoGmMatch = ep?.event_role === 'co_gm' &&
          (!factionId || ep?.co_gm_faction_id === factionId || ep?.faction_id === factionId)
        const isGmMatch = ep?.event_role === 'gm' &&
          (!factionId || ep?.faction_id === factionId)
        let isFactionCo = false
        if (!isCoGmMatch && !isGmMatch) {
          const { data: coRows } = await supabase
            .from('mcs_nodes').select('faction_id')
            .eq('event_id', activeEventId).is('parent_id', null)
            .eq('assigned_player_id', user.id).limit(1)
          isFactionCo = coRows?.[0] && (!factionId || coRows[0].faction_id === factionId)
        }
        if (!isCoGmMatch && !isGmMatch && !isFactionCo) return

        const pref = await getPrefs(user.id)
        if (!prefAllows(pref, 'frago_broadcast')) return
        show(frago.mission_name || 'Objective complete',
          newRow.notify_config.message, `/frago/${fragoId}`)
      })
    // events status changes — broadcast to every subscriber on this event
    .on('postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'events', filter: `id=eq.${activeEventId}` },
      async (payload) => {
        if (payload.old?.status === payload.new?.status) return
        if (!['live','endex','closed'].includes(payload.new?.status)) return
        const pref = await getPrefs(user.id)
        if (!prefAllows(pref, 'game_status')) return
        const titleMap = {
          live:   'GO LIVE — event is now active',
          endex:  'ENDEX called',
          closed: 'Event closed — AAR available',
        }
        const url = payload.new.status === 'closed'
          ? `/event/${activeEventId}/dossiers` : '/play'
        show(titleMap[payload.new.status],
          `${payload.new.name || 'Operation'} status changed to ${payload.new.status}.`,
          url)
      })
    .subscribe()

  return () => {
    try { supabase.removeChannel(channel) } catch { /* ignore */ }
  }
}

// Reset the prefs cache — call on sign-out or when the Profile settings
// tab toggles a value so the next emit reads fresh truth.
export function clearForegroundNotifyCache() {
  cachedPrefs = null
  cachedPrefsUserId = null
}
